import { api } from './api.js';

export function createOperationObserver({ read, hidden = () => document.hidden, now = Date.now, setTimer = setTimeout, clearTimer = clearTimeout }) {
  const entries = new Map();
  let timer = null, running = false, epoch = 0;
  function prune() {
    for (const [id, entry] of entries) {
      for (const watcher of entry.watchers) if (!watcher.isActive()) { watcher.resolve(null); entry.watchers.delete(watcher); }
      if (!entry.watchers.size) entries.delete(id);
    }
  }
  function arm() {
    clearTimer(timer); timer = null; prune();
    if (running || hidden() || !entries.size) return;
    timer = setTimer(() => void poll(), Math.max(0, Math.min(...[...entries.values()].map(entry => entry.due)) - now()));
  }
  async function poll() {
    timer = null; prune();
    if (hidden() || running) return;
    const due = [...entries.values()].filter(entry => entry.due <= now());
    if (!due.length) return arm();
    running = true; const generation = epoch;
    try {
      const statuses = await read(due.map(entry => entry.id));
      if (generation !== epoch) return;
      const byId = new Map(statuses.map(operation => [operation.id, operation]));
      prune();
      for (const entry of due) {
        if (entries.get(entry.id) !== entry) continue;
        const operation = byId.get(entry.id);
        if (operation) for (const watcher of entry.watchers) {
          watcher.onStatus?.(operation);
          if (watcher.terminal.includes(operation.status)) { watcher.resolve(operation); entry.watchers.delete(watcher); }
        }
        entry.attempt++; entry.due = now() + (entry.attempt === 1 ? 2000 : 5000);
      }
    } catch (error) {
      if (generation !== epoch) return;
      for (const entry of due) {
        if (entries.get(entry.id) !== entry) continue;
        for (const watcher of entry.watchers) {
          if (error.status && error.status < 500) { watcher.reject(error); entry.watchers.delete(watcher); }
          else watcher.onError?.(error);
        }
        entry.due = now() + 5000;
      }
    } finally { running = false; arm(); }
  }
  return {
    observe(id, { isActive = () => true, onStatus, onError, terminal = ['SUCCEEDED', 'FAILED', 'BLOCKED'] } = {}) {
      if (!id) return Promise.reject(new Error('Identificativo dell’operazione mancante.'));
      let entry = entries.get(id);
      if (!entry) { entry = { id, attempt: 0, due: now() + 1000, watchers: new Set() }; entries.set(id, entry); }
      return new Promise((resolve, reject) => { entry.watchers.add({ resolve, reject, isActive, onStatus, onError, terminal }); arm(); });
    },
    wake(id) { const entry = entries.get(id); if (entry) entry.due = now(); arm(); },
    visibilityChanged() { for (const entry of entries.values()) entry.due = now(); arm(); },
    reset() { epoch++; clearTimer(timer); timer = null; for (const entry of entries.values()) for (const watcher of entry.watchers) watcher.resolve(null); entries.clear(); }
  };
}

export const operationObserver = createOperationObserver({ read: async ids => {
  const chunks = [];
  for (let start = 0; start < ids.length; start += 100) chunks.push(ids.slice(start, start + 100));
  const responses = await Promise.all(chunks.map(ids => api('/operations/status', { method: 'POST', body: { ids } })));
  return responses.flatMap(response => response.data || []);
} });
